/**
 * Where each place sits on the World Map (lane AK, plan 22). Config, not code:
 * the places and roads come from the authored portal graph
 * (shared/logic/portalGraph.ts); this file only says where to draw them and
 * which road is the main one. A zone the walk reaches but this file does not
 * place is listed as unplaced by the model and fails worldMapLayout.test.ts.
 *
 * Coordinates are in the map's own viewBox units (VIEW below), x east, y south.
 * They are atlas positions, separate from playable level geometry: a place can
 * move here without touching its level. The main road arcs west to east over the
 * Hollow Coil's island; the land over the sea sits apart to the east (plan 28).
 */

export type WorldMapLabelSide = 'above' | 'below' | 'left' | 'right';

export interface WorldMapPlacement {
  x: number;
  y: number;
  /** Where the name sits relative to the marker (default: below). */
  label?: WorldMapLabelSide;
}

/** One soft watercolour wash (an ellipse in view units). Washes have no hard edge. */
export interface WorldMapWash { cx: number; cy: number; rx: number; ry: number }

/**
 * A landmass drawn under its places (plan 28 D54). Illustration only: membership
 * is a drawing choice, not game data, and the washes add no road or gate.
 * worldMapLayout.test.ts checks every place sits on exactly its own land.
 */
export interface WorldMapRegion {
  id: string;
  members: readonly string[];
  washes: readonly WorldMapWash[];
  /** Caption over the land; a provisional name says so. */
  label?: { text: string; x: number; y: number };
}

export interface WorldMapLayout {
  /** The map the walk starts from: every experience enters at the Waking Vault. */
  entryMapId: string;
  /** Title over the map. */
  title: string;
  view: { width: number; height: number };
  /** The story road, in travel order. Consecutive pairs draw as the main road;
   *  cross-checked against status/map-program.json mainRoute in a test. */
  mainRoute: readonly string[];
  places: Readonly<Record<string, WorldMapPlacement>>;
  /** Landmasses under the places (none: a plain sheet). */
  regions?: readonly WorldMapRegion[];
  /** Roads drawn as a sea crossing (keyed by portalPairKey), with the two cubic
   *  control points the drawing bends through. The portal pair is still the authority. */
  seaCrossings?: Readonly<Record<string, { via: readonly [{ x: number; y: number }, { x: number; y: number }] }>>;
}

/** The Hollow Coil's island (west) and the land over the sea (east). */
export const HOME_PLACES = [
  'tomb_waking', 'village_arrival', 'first_field', 'moonfall_hollow', 'tidewend', 'tidewend_port', 'tidewend_outskirts',
  'moonfen_pasture', 'drowned_road', 'pig_beach', 'lanternwood', 'lanternroot_hollow', 'glowspore_grotto',
  'brackenmoor', 'coil_undercroft',
] as const;
export const OVERSEAS_PLACES = [
  'wickhaven', 'wickhaven_port', 'dark_forest', 'deep_woods', 'abandoned_camp', 'old_grove',
  'ancient_temple', 'volcano_summit', 'frozen_lake', 'crystal_caverns', 'sunken_ruins',
] as const;

/** Optional land-road controls use canonical pair order. The forest journey
 * now forms a compact overseas circuit, so its roads need no detour curves. */
export const WORLD_MAP_LAND_ROAD_CONTROLS: Readonly<Record<string, readonly [{ x: number; y: number }, { x: number; y: number }]>> = {
};

export const WORLD_MAP_LAYOUT: WorldMapLayout = {
  entryMapId: 'tomb_waking',
  title: 'The Hollow Coil',
  // 600 tall: lane BG's 700 shrank every name by a seventh (lane BJ).
  view: { width: 1000, height: 600 },
  mainRoute: [
    'tomb_waking', 'village_arrival', 'first_field', 'tidewend', 'tidewend_outskirts',
    'drowned_road', 'lanternwood', 'brackenmoor', 'coil_undercroft',
  ],
  // Plan 28 (D51-D54, design candidate dj-watercolors 61db155): the Hollow Coil's
  // island to the west, the main road arcing over it from the Waking Vault to the
  // Coil; Brinebristle Strand on the coast below the Drowned Road, the alternate
  // road from the Outskirts to Lanternwood; the ferry from Tidewend Harbor round
  // the south coast and over the sea to Wickhaven Port, with the old world around it.
  places: {
    tomb_waking: { x: 60, y: 410, label: 'below' },
    village_arrival: { x: 75, y: 300, label: 'below' },
    first_field: { x: 130, y: 200, label: 'above' },
    moonfall_hollow: { x: 190, y: 95, label: 'above' },
    tidewend: { x: 235, y: 305, label: 'below' },
    tidewend_port: { x: 235, y: 400, label: 'below' },
    tidewend_outskirts: { x: 300, y: 215, label: 'above' },
    moonfen_pasture: { x: 370, y: 70, label: 'above' },
    drowned_road: { x: 390, y: 280, label: 'below' },
    pig_beach: { x: 400, y: 400, label: 'below' },
    lanternwood: { x: 480, y: 230, label: 'above' },
    lanternroot_hollow: { x: 520, y: 145, label: 'left' },
    glowspore_grotto: { x: 590, y: 60, label: 'below' },
    brackenmoor: { x: 570, y: 300, label: 'below' },
    coil_undercroft: { x: 560, y: 420, label: 'below' },
    // Over the sea: the forest journey makes a readable loop. Cold and cavern
    // branches sit north of it; the home-island coordinates stay unchanged.
    wickhaven: { x: 710, y: 370, label: 'left' },
    wickhaven_port: { x: 710, y: 490, label: 'below' },
    dark_forest: { x: 805, y: 465, label: 'below' },
    deep_woods: { x: 940, y: 455, label: 'below' },
    abandoned_camp: { x: 940, y: 300, label: 'left' },
    old_grove: { x: 805, y: 230, label: 'left' },
    ancient_temple: { x: 705, y: 75, label: 'above' },
    volcano_summit: { x: 850, y: 70, label: 'below' },
    frozen_lake: { x: 880, y: 165, label: 'right' },
    crystal_caverns: { x: 760, y: 170, label: 'left' },
    sunken_ruins: { x: 690, y: 250, label: 'left' },
  },
  regions: [
    {
      id: 'hollow_coil',
      members: HOME_PLACES,
      washes: [
        { cx: 300, cy: 230, rx: 300, ry: 200 },
        { cx: 110, cy: 330, rx: 110, ry: 120 },
        { cx: 500, cy: 360, rx: 150, ry: 110 },
        { cx: 500, cy: 100, rx: 130, ry: 90 },
      ],
    },
    {
      id: 'overseas',
      members: OVERSEAS_PLACES,
      washes: [
        { cx: 830, cy: 375, rx: 155, ry: 195 },
        { cx: 825, cy: 175, rx: 150, ry: 145 },
        { cx: 760, cy: 105, rx: 80, ry: 70 },
        { cx: 740, cy: 235, rx: 65, ry: 75 },
      ],
      // Provisional until DJ names the continent (plan 28 D54): no invented lore.
      label: { text: 'Over the Sea (name to come)', x: 835, y: 27 },
    },
  ],
  seaCrossings: {
    // The Tidewend dock ferry (plan 27 D50): down the south coast, over the sea.
    'tidewend_port|wickhaven_port': { via: [{ x: 230, y: 600 }, { x: 640, y: 580 }] },
  },
};
